import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '@/components/feature/AppShell';
import { teacherNotifications } from '@/mocks/dashboard';
import { students as allStudents, type StudentRecord } from '@/mocks/students';
import StudentFilters from '@/pages/dashboard/admin/students/components/StudentFilters';
import StudentTable from '@/pages/dashboard/admin/students/components/StudentTable';
import EnrollmentModal from '@/pages/dashboard/admin/students/components/EnrollmentModal';
import StudentDetailModal from '@/pages/dashboard/admin/students/components/StudentDetailModal';
import { incidentLogs } from '@/mocks/preInspection';

const LOGGED_IN_TEACHER = 'Mr. James Okafor';

export default function TeacherStudentsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showIncidentsOnly, setShowIncidentsOnly] = useState(false);
  const [studentData, setStudentData] = useState<StudentRecord[]>(allStudents);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentRecord | null>(null);

  // Pre-filter: only students assigned to this teacher
  const teacherStudents = useMemo(
    () => studentData.filter((s) => s.classTeacher === LOGGED_IN_TEACHER),
    [studentData],
  );

  // Incident student IDs (from pre-filtered pool)
  const incidentStudentIds = useMemo(() => {
    const ids = new Set<string>();
    incidentLogs.forEach((inc) => ids.add(inc.studentId));
    return ids;
  }, []);

  const totalIncidentStudents = useMemo(
    () => teacherStudents.filter((s) => incidentStudentIds.has(s.studentId)).length,
    [teacherStudents, incidentStudentIds],
  );

  const filteredStudents = useMemo(() => {
    let result = teacherStudents;
    if (!showInactive) {
      result = result.filter((s) => s.status !== 'inactive');
    }
    if (showIncidentsOnly) {
      result = result.filter((s) => incidentStudentIds.has(s.studentId));
    }
    if (selectedClass) {
      result = result.filter((s) => s.className === selectedClass);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.studentId.toLowerCase().includes(q) ||
          s.fatherName.toLowerCase().includes(q) ||
          s.motherName.toLowerCase().includes(q) ||
          s.aadhaarNumber.includes(q) ||
          s.udidNumber.toLowerCase().includes(q),
      );
    }
    return result;
  }, [teacherStudents, selectedClass, search, showInactive, showIncidentsOnly, incidentStudentIds]);

  const handleSaveStudent = (updated: StudentRecord) => {
    setStudentData((prev) =>
      prev.map((s) => (s.id === updated.id ? updated : s)),
    );
  };

  const handleStudentStatusChange = (student: StudentRecord, newStatus: string) => {
    setStudentData((prev) =>
      prev.map((s) =>
        s.id === student.id
          ? { ...s, status: newStatus as StudentRecord['status'], deactivatedAt: undefined }
          : s,
      ),
    );
  };

  return (
    <AppShell
      pageTitle={t('students.myStudentsTitle')}
      pageSubtitle={t('students.myStudentsSubtitle')}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), onClick: () => navigate('/dashboard/teacher-dashboard') },
        { label: t('students.myStudentsTitle') },
      ]}
      notifications={teacherNotifications}
    >
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-sm text-foreground-500 font-label">
            <span dangerouslySetInnerHTML={{ __html: t('students.teacherTotalEnrolled', { count: teacherStudents.length, interpolation: { escapeValue: false } }) }} />
          </p>
        </div>
      </div>

      {/* Filters + Add */}
      <StudentFilters
        search={search}
        onSearchChange={setSearch}
        selectedClass={selectedClass}
        onClassChange={setSelectedClass}
        showInactive={showInactive}
        onToggleInactive={setShowInactive}
        showIncidentsOnly={showIncidentsOnly}
        onToggleIncidents={setShowIncidentsOnly}
        incidentCount={totalIncidentStudents}
        onAddStudent={() => setEnrollmentOpen(true)}
      />

      {/* Student Table */}
      <StudentTable
        students={filteredStudents}
        onRowClick={setSelectedStudent}
        onStatusChange={handleStudentStatusChange}
      />

      {/* Enrollment Modal */}
      <EnrollmentModal
        open={enrollmentOpen}
        onClose={() => setEnrollmentOpen(false)}
        onSaveDraft={() => setEnrollmentOpen(false)}
        onSubmit={() => setEnrollmentOpen(false)}
      />

      {/* Student Detail Modal */}
      <StudentDetailModal
        open={!!selectedStudent}
        student={selectedStudent}
        onClose={() => setSelectedStudent(null)}
        onSave={handleSaveStudent}
      />
    </AppShell>
  );
}